/**
 * AppointmentSelector Component (Wizard Edition)
 * Shows consultations with inline editable fields and visibility toggles.
 * Each field has a show/hide checkbox. Data is editable.
 * Each consultation has a Reset button to revert to original DB data.
 */

import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Check,
  Calendar,
  Clock,
  FileText,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Eye,
  EyeOff,
  Stethoscope,
  Pill,
  Activity,
  CheckCircle,
  File,
  Info,
} from "lucide-react";
import { EXPORT_FIELDS } from "../../services/medicalReportService";

// Category icon mapping
const CATEGORY_ICONS = {
  general: Info,
  observations: Stethoscope,
  prescriptions: Pill,

  charts: Activity,
  careSteps: CheckCircle,
  documents: File,
  medicalBackground: FileText,
};

const AppointmentSelector = ({
  appointments,
  originalAppointments,
  selectedAppointments,
  onToggleAppointment,
  fieldVisibility,
  onToggleFieldVisibility,
  onEditField,
  onResetAppointment,
  collapsedAppointments,
  onToggleCollapse,
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = useCallback((aptId, cat) => {
    const key = `${aptId}_${cat}`;
    setExpandedCategories(prev => ({ ...prev, [key]: prev[key] === false ? true : (prev[key] === undefined ? false : !prev[key]) }));
  }, []);

  const isFieldVisible = useCallback((aptId, category, fieldKey) => {
    return fieldVisibility?.[aptId]?.[category]?.[fieldKey] ?? true;
  }, [fieldVisibility]);

  // Check if appointment data has been modified from original
  const isModified = useCallback((appointmentId) => {
    const original = originalAppointments?.find(a => String(a.id) === String(appointmentId));
    const edited = appointments?.find(a => String(a.id) === String(appointmentId));
    if (!original || !edited) return false;
    return JSON.stringify(original) !== JSON.stringify({ ...edited, careSteps: undefined, charts: undefined, documents: undefined });
  }, [appointments, originalAppointments]);

  // --- Field value access ---
  const getFieldValue = (appointment, category, fieldKey) => {
    if (category === "general") return appointment[fieldKey] ?? "";
    if (category === "observations") return appointment.observations?.[0]?.[fieldKey] ?? "";
    return "";
  };

  // --- Check if category has data ---
  const categoryHasData = (appointment, category) => {
    switch (category) {
      case "general": return true;
      case "observations": return appointment.observations?.length > 0;
      case "prescriptions": return appointment.prescription_content?.length > 0;

      case "careSteps": return appointment.careSteps?.length > 0;
      case "charts": return appointment.charts?.length > 0;
      case "documents": return appointment.documents?.length > 0;
      case "medicalBackground": return false;
      default: return false;
    }
  };

  // Get array data for a category
  const getCategoryArrayData = (appointment, category) => {
    switch (category) {
      case "prescriptions": return appointment.prescription_content || [];

      case "careSteps": return appointment.careSteps || [];
      case "charts": return appointment.charts || [];
      case "documents": return appointment.documents || [];
      default: return null;
    }
  };

  // Get the edit path prefix for array-based categories
  const getArrayKeyForCategory = (category) => {
    switch (category) {
      case "prescriptions": return "prescription_content";

      case "careSteps": return "careSteps";
      case "charts": return "charts";
      case "documents": return "documents";
      default: return category;
    }
  };

  // --- Render helpers ---

  // Visibility toggle button
  const VisibilityToggle = ({ aptId, category, fieldKey }) => {
    const visible = isFieldVisible(aptId, category, fieldKey);
    return (
      <button
        type="button"
        className={`visibility-toggle ${visible ? "visibility-toggle--visible" : "visibility-toggle--hidden"}`}
        onClick={() => onToggleFieldVisibility(aptId, category, fieldKey)}
        title={visible ? "Masquer ce champ" : "Afficher ce champ"}
      >
        {visible ? <Eye size={13} /> : <EyeOff size={13} />}
      </button>
    );
  };

  // Render a single editable field row (general / observations)
  const renderFieldRow = (appointment, category, field) => {
    const value = getFieldValue(appointment, category, field.key);
    const visible = isFieldVisible(appointment.id, category, field.key);
    const editPath = category === "general" ? field.key : `observations.0.${field.key}`;
    const isLongText = ["examination", "other_notes", "reminder_note"].includes(field.key);
    const isReadOnly = ["appointment_date", "appointment_time"].includes(field.key);
    const isHTML = ["examination", "other_notes", "reminder_note"].includes(field.key);

    return (
      <div key={field.key} className={`field-row ${visible ? "field-row--visible" : "field-row--hidden"}`}>
        <VisibilityToggle aptId={appointment.id} category={category} fieldKey={field.key} />
        <span className="field-row-label">{field.label}</span>
        <div className="field-row-value">
          {isLongText ? (
            isHTML && value ? (
              <div
                className={`field-html-editable ${isReadOnly ? "field-html-editable--readonly" : ""}`}
                contentEditable={!isReadOnly && visible}
                suppressContentEditableWarning
                onBlur={(e) => { if (!isReadOnly && visible) onEditField(appointment.id, editPath, e.target.innerHTML); }}
                dangerouslySetInnerHTML={{ __html: value }}
              />
            ) : (
              <textarea
                className={`field-textarea ${isReadOnly ? "field-textarea--readonly" : ""}`}
                value={value}
                readOnly={isReadOnly || !visible}
                onChange={(e) => onEditField(appointment.id, editPath, e.target.value)}
                rows={3}
              />
            )
          ) : (
            <input
              type="text"
              className={`field-input ${isReadOnly ? "field-input--readonly" : ""}`}
              value={value}
              readOnly={isReadOnly || !visible}
              onChange={(e) => onEditField(appointment.id, editPath, e.target.value)}
            />
          )}
        </div>
      </div>
    );
  };

  // Render array-based section (prescriptions, care steps, etc.)
  const renderArrayFields = (appointment, category, items, fieldDefs) => {
    if (!items || items.length === 0) {
      return (
        <div className="array-empty">
          Aucune {EXPORT_FIELDS[category]?.label?.toLowerCase() || 'donnée'} disponible
        </div>
      );
    }

    const arrayKey = getArrayKeyForCategory(category);

    return items.map((item, idx) => (
      <div key={idx} className={`array-item ${idx < items.length - 1 ? "array-item--separator" : ""}`}>
        <div className="array-item-label">
          {EXPORT_FIELDS[category]?.label} #{idx + 1}
        </div>
        {fieldDefs.map(field => {
          const visible = isFieldVisible(appointment.id, category, field.key);
          const value = item[field.key] ?? "";
          const editPath = `${arrayKey}.${idx}.${field.key}`;
          const isLong = ["prescription_body", "comment", "description"].includes(field.key);
          const isReadOnly = ["created_at", "prescription_date", "measured_at", "file_type"].includes(field.key);
          const isHTML = ["prescription_body"].includes(field.key);

          return (
            <div key={field.key} className={`field-row field-row--array ${visible ? "field-row--visible" : "field-row--hidden"}`}>
              <VisibilityToggle aptId={appointment.id} category={category} fieldKey={field.key} />
              <span className="field-row-label field-row-label--array">{field.label}</span>
              <div className="field-row-value">
                {isLong ? (
                  isHTML && value ? (
                    <div
                      className={`field-html-editable field-html-editable--small ${isReadOnly ? "field-html-editable--readonly" : ""}`}
                      contentEditable={!isReadOnly && visible}
                      suppressContentEditableWarning
                      onBlur={(e) => { if (!isReadOnly && visible) onEditField(appointment.id, editPath, e.target.innerHTML); }}
                      dangerouslySetInnerHTML={{ __html: value }}
                    />
                  ) : (
                    <textarea
                      className={`field-textarea ${isReadOnly ? "field-textarea--readonly" : ""}`}
                      value={typeof value === 'string' ? value : String(value)}
                      readOnly={isReadOnly || !visible}
                      onChange={(e) => onEditField(appointment.id, editPath, e.target.value)}
                      rows={2}
                    />
                  )
                ) : field.key === "is_done" ? (
                  <label className="field-boolean">
                    <input
                      type="checkbox"
                      checked={!!value}
                      disabled={!visible}
                      onChange={(e) => onEditField(appointment.id, editPath, e.target.checked)}
                    />
                    <span className={`field-boolean__label ${value ? "field-boolean__label--done" : "field-boolean__label--pending"}`}>
                      {value ? "Fait" : "En attente"}
                    </span>
                  </label>
                ) : (field.key === "amount" || field.key === "rest_topay") ? (
                  <div className="field-amount-wrapper">
                    <input
                      type="number"
                      className={`field-input field-input--number ${isReadOnly ? "field-input--readonly" : ""}`}
                      value={value}
                      readOnly={isReadOnly || !visible}
                      onChange={(e) => onEditField(appointment.id, editPath, e.target.value)}
                    />
                    <span className="field-amount-wrapper__suffix">DA</span>
                  </div>
                ) : (
                  <input
                    type="text"
                    className={`field-input ${isReadOnly ? "field-input--readonly" : ""}`}
                    value={typeof value === 'string' ? value : String(value)}
                    readOnly={isReadOnly || !visible}
                    onChange={(e) => onEditField(appointment.id, editPath, e.target.value)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  // --- Main render ---

  if (!appointments || appointments.length === 0) {
    return (
      <div className="consultation-empty">
        <Calendar size={48} />
        <p>Aucune consultation trouvée pour ce patient</p>
      </div>
    );
  }

  return (
    <div className="consultation-list">
      {appointments.map((appointment) => {
        const isSelected = !!selectedAppointments[appointment.id];
        const isCollapsed = !!collapsedAppointments[appointment.id];
        const modified = isModified(appointment.id);
        const appointmentDate = appointment.appointment_date || appointment.date || appointment.created_at;
        const conditionTitle = appointment.observations?.[0]?.medical_condition_title;

        return (
          <div
            key={appointment.id}
            className={`consultation-card ${isSelected ? "consultation-card--selected" : "consultation-card--unselected"}`}
          >
            {/* === Consultation Header === */}
            <div className={`consultation-header ${isSelected ? "consultation-header--selected" : "consultation-header--unselected"} ${isCollapsed ? "consultation-header--collapsed" : "consultation-header--expanded"}`}>
              {/* Selection checkbox */}
              <label className="consultation-checkbox">
                <input type="checkbox" checked={isSelected} onChange={() => onToggleAppointment(appointment.id)} />
                <span className={`consultation-checkbox-mark ${isSelected ? "consultation-checkbox-mark--checked" : "consultation-checkbox-mark--unchecked"}`}>
                  {isSelected && <Check size={15} />}
                </span>
              </label>

              {/* Date badge */}
              <div className="consultation-date-badge">
                <span className="consultation-date-badge__month">
                  {appointmentDate ? moment(appointmentDate).format("MMM") : "N/A"}
                </span>
                <span className="consultation-date-badge__day">
                  {appointmentDate ? moment(appointmentDate).format("DD") : "--"}
                </span>
                <span className="consultation-date-badge__year">
                  {appointmentDate ? moment(appointmentDate).format("YYYY") : "----"}
                </span>
              </div>

              {/* Consultation info */}
              <div className="consultation-info">
                <h4 className="consultation-info__title">
                  {conditionTitle || appointment.reason || appointment.status || `Consultation n°${appointment.id}`}
                </h4>
                <div className="consultation-info__meta">
                  {appointmentDate && (
                    <span className="consultation-meta-time">
                      <Clock size={11} /> {moment(appointmentDate).format("HH:mm")}
                    </span>
                  )}
                  {appointment.appointment_type && (
                    <span className="consultation-meta-badge consultation-meta-badge--type">
                      {appointment.appointment_type}
                    </span>
                  )}
                  {appointment.prescription_content?.length > 0 && (
                    <span className="consultation-meta-badge consultation-meta-badge--rx">
                      {appointment.prescription_content.length} Rx
                    </span>
                  )}
                  {modified && (
                    <span className="consultation-meta-badge consultation-meta-badge--modified">
                      Modifié
                    </span>
                  )}
                </div>
              </div>

              {/* Reset button */}
              <button
                type="button"
                className={`consultation-reset-btn ${modified ? "consultation-reset-btn--modified" : "consultation-reset-btn--clean"}`}
                onClick={() => onResetAppointment(appointment.id)}
                title="Réinitialiser aux données d'origine"
              >
                <RotateCcw size={13} /> Réinitialiser
              </button>

              {/* Collapse toggle */}
              <button
                type="button"
                className="consultation-collapse-btn"
                onClick={() => onToggleCollapse(appointment.id)}
              >
                {isCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
              </button>
            </div>

            {/* === Consultation Body - Data Categories === */}
            {!isCollapsed && (
              <div>
                {Object.entries(EXPORT_FIELDS).map(([catKey, catDef]) => {
                  if (!categoryHasData(appointment, catKey)) return null;

                  const expKey = `${appointment.id}_${catKey}`;
                  const isCatExpanded = expandedCategories[expKey] !== false;
                  const Icon = CATEGORY_ICONS[catKey] || FileText;
                  const arrayData = getCategoryArrayData(appointment, catKey);

                  return (
                    <div key={catKey} className="category-section">
                      {/* Category Header */}
                      <button
                        type="button"
                        className={`category-header-btn ${isCatExpanded ? "category-header-btn--expanded" : "category-header-btn--collapsed"}`}
                        onClick={() => toggleCategory(appointment.id, catKey)}
                      >
                        <Icon size={15} className="category-icon" />
                        <span className="category-header-btn__label">{catDef.label}</span>
                        {arrayData && arrayData.length > 0 && (
                          <span className="category-header-btn__count">({arrayData.length})</span>
                        )}
                        {isCatExpanded ? <ChevronUp size={14} color="#aaa" /> : <ChevronDown size={14} color="#aaa" />}
                      </button>

                      {/* Category Content */}
                      {isCatExpanded && (
                        <div className="category-content">
                          {(catKey === "general" || catKey === "observations") ? (
                            catDef.fields.map(field => renderFieldRow(appointment, catKey, field))
                          ) : arrayData ? (
                            renderArrayFields(appointment, catKey, arrayData, catDef.fields)
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

AppointmentSelector.propTypes = {
  appointments: PropTypes.array.isRequired,
  originalAppointments: PropTypes.array,
  selectedAppointments: PropTypes.object.isRequired,
  onToggleAppointment: PropTypes.func.isRequired,
  fieldVisibility: PropTypes.object,
  onToggleFieldVisibility: PropTypes.func.isRequired,
  onEditField: PropTypes.func.isRequired,
  onResetAppointment: PropTypes.func.isRequired,
  collapsedAppointments: PropTypes.object,
  onToggleCollapse: PropTypes.func.isRequired,
};

AppointmentSelector.defaultProps = {
  originalAppointments: [],
  fieldVisibility: {},
  collapsedAppointments: {},
};

export default AppointmentSelector;
